// Device preferences: how the portal looks on THIS device, remembered in
// localStorage. They are a documented exception to CLAUDE.md #9 (Nav,
// 2026-09-27): they describe the broker's screen, not their business, so they
// have no place in PMS and are harmless to lose. That is also why every read
// falls back to a default and no write ever throws: storage may be missing,
// blocked (a private window, a strict browser setting) or full.

import { isThemeChoice, type ThemeChoice } from "@/theme/themeChoice";

/** The sidebar's collapsed state (BRK-S244): "collapsed" | "expanded". */
export const SIDEBAR_STORAGE_KEY = "brk:sidebar";

/** The theme choice (BRK-S245): "day" | "night" | "system". index.html's
 *  pre-paint script reads the same key. */
export const THEME_STORAGE_KEY = "brk:theme";

/** Whether a form's header is folded (BRK-S266): "collapsed" | "expanded".
 *  One choice for every form on the device. */
export const FORM_HEADER_STORAGE_KEY = "brk:formHeader";

type Readable = Pick<Storage, "getItem">;
type Writable = Pick<Storage, "setItem">;

/** The device's localStorage, or null where even touching it throws. */
function deviceStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Whether the broker collapsed the sidebar on this device. Expanded unless
 *  they chose otherwise. */
export function readSidebarCollapsed(
  storage: Readable | null = deviceStorage(),
): boolean {
  try {
    return storage?.getItem(SIDEBAR_STORAGE_KEY) === "collapsed";
  } catch {
    return false;
  }
}

export function writeSidebarCollapsed(
  collapsed: boolean,
  storage: Writable | null = deviceStorage(),
): void {
  try {
    storage?.setItem(SIDEBAR_STORAGE_KEY, collapsed ? "collapsed" : "expanded");
  } catch {
    /* storage refused: the choice lasts until the page reloads */
  }
}

/** Whether the broker folded the forms' headers on this device. Expanded
 *  unless they chose otherwise. */
export function readFormHeaderCollapsed(
  storage: Readable | null = deviceStorage(),
): boolean {
  try {
    return storage?.getItem(FORM_HEADER_STORAGE_KEY) === "collapsed";
  } catch {
    return false;
  }
}

export function writeFormHeaderCollapsed(
  collapsed: boolean,
  storage: Writable | null = deviceStorage(),
): void {
  try {
    storage?.setItem(
      FORM_HEADER_STORAGE_KEY,
      collapsed ? "collapsed" : "expanded",
    );
  } catch {
    /* storage refused: the choice lasts until the page reloads */
  }
}

/** The broker's theme choice on this device. System unless they chose. */
export function readThemeChoice(
  storage: Readable | null = deviceStorage(),
): ThemeChoice {
  try {
    const stored = storage?.getItem(THEME_STORAGE_KEY);
    return isThemeChoice(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function writeThemeChoice(
  choice: ThemeChoice,
  storage: Writable | null = deviceStorage(),
): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    /* storage refused: the choice lasts until the page reloads */
  }
}
